import React, { useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Popover,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    ExclamationCircleOutlined,
    UndoOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

export const getPendingReextractSuggestions = (record = {}) => {
    const raw = record?.reextract_suggestions || record?.recognition_result?._reextract_suggestions;
    if (!raw || typeof raw !== 'object') return { voidedSuggestion: null, fieldItems: [] };
    let voidedSuggestion = null;
    const fieldItems = [];
    Object.entries(raw).forEach(([field, entry]) => {
        if (!entry || typeof entry !== 'object') return;
        const decision = entry.decision || 'pending';
        if (decision !== 'pending') return;
        if (field === '_page_voided') {
            voidedSuggestion = entry;
        } else {
            fieldItems.push({
                field,
                oldValue: entry.old_value ?? entry.current_value ?? null,
                newValue: entry.new_value ?? entry.suggested_value ?? null,
                source: entry.source || 'reextract',
            });
        }
    });
    return { voidedSuggestion, fieldItems };
};

export const getFieldModelAdjustment = (record = {}, field) => {
    const adjustments = record?.model_adjustments || record?.recognition_result?._model_adjustments;
    if (!adjustments || typeof adjustments !== 'object') return null;
    const item = adjustments[field];
    return item && typeof item === 'object' ? item : null;
};

export const isFieldUnconfirmed = (record = {}, field) => {
    const unconfirmed = Array.isArray(record?.unconfirmed_fields) ? record.unconfirmed_fields : [];
    return unconfirmed.includes(field);
};

export const buildAdjustmentSummaryText = (adj, field, record) => {
    if (!adj) {
        return `字段 ${field} 置信度偏低或经过模型工具修正，待人工复核确认`;
    }
    const parts = [];
    if (adj.raw_read !== undefined) parts.push(`原读值: ${adj.raw_read ?? '空'}`);
    if (adj.final !== undefined) parts.push(`预填值: ${adj.final ?? record?.[field] ?? '空'}`);
    if (adj.reason) parts.push(`理由: ${adj.reason}`);
    if (adj.confidence != null) parts.push(`置信度: ${Math.round(Number(adj.confidence) * 100)}%`);
    if (Array.isArray(adj.evidence_tool_call_ids) && adj.evidence_tool_call_ids.length > 0) {
        parts.push(`证据: ${adj.evidence_tool_call_ids.join(', ')}`);
    }
    return parts.join('；');
};

export const UnconfirmedFieldBadge = ({
    record,
    field,
    notModelError = false,
    disabled = false,
    onDecide,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const unconfirmed = isFieldUnconfirmed(record, field);
    const adj = getFieldModelAdjustment(record, field);
    if (!unconfirmed) return null;

    const handleDecision = async (decision) => {
        if (!onDecide) return;
        setSubmitting(true);
        try {
            await onDecide(field, decision, notModelError);
        } finally {
            setSubmitting(false);
        }
    };

    const content = (
        <Space direction="vertical" size={6} style={{ maxWidth: 320 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
                {adj
                    ? 'AI 发现纸面字迹存疑，结合航班/机队数据为您预填了修正值，请核对确认：'
                    : '该项字迹模糊、AI 识别把握较低，请核对原件确认：'}
            </Text>
            <div>
                <Text type="secondary" style={{ fontSize: 12 }}>纸面原读值：</Text>
                <Text code>{adj?.raw_read != null ? String(adj.raw_read) : '空'}</Text>
            </div>
            <div>
                <Text type="secondary" style={{ fontSize: 12 }}>当前预填值：</Text>
                <Text strong>{adj?.final != null ? String(adj.final) : String(record?.[field] ?? '空')}</Text>
            </div>
            {adj?.reason && (
                <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>修正依据：</Text>
                    <Text style={{ fontSize: 12 }}>{adj.reason}</Text>
                </div>
            )}
            {adj?.confidence != null && (
                <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>识别把握度：</Text>
                    <Text style={{ fontSize: 12 }}>{`${Math.round(Number(adj.confidence) * 100)}%`}</Text>
                </div>
            )}
            {Array.isArray(adj?.evidence_tool_call_ids) && adj.evidence_tool_call_ids.length > 0 && (
                <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>核对记录：</Text>
                    <Text code style={{ fontSize: 11 }}>{adj.evidence_tool_call_ids.join(', ')}</Text>
                </div>
            )}
            {onDecide && (
                <Space size={8} style={{ marginTop: 4 }}>
                    <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        loading={submitting}
                        disabled={disabled}
                        onClick={() => handleDecision('accepted')}
                    >
                        采纳预填
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<UndoOutlined />}
                        loading={submitting}
                        disabled={disabled}
                        onClick={() => handleDecision('rejected')}
                    >
                        一键撤回原读值
                    </Button>
                </Space>
            )}
        </Space>
    );

    if (!onDecide) {
        return (
            <Tooltip title={buildAdjustmentSummaryText(adj, field, record)}>
                <Tag color="warning" style={{ margin: 0, lineHeight: '16px', padding: '0 4px' }}>
                    未确认
                </Tag>
            </Tooltip>
        );
    }

    return (
        <Popover title={`未确认字段：${field}`} content={content} trigger="hover">
            <Tag
                color="warning"
                icon={<ExclamationCircleOutlined />}
                style={{ margin: 0, lineHeight: '16px', cursor: 'pointer' }}
            >
                未确认
            </Tag>
        </Popover>
    );
};

export const ReextractSuggestionsBanner = ({
    record,
    fieldLabels = {},
    disabled = false,
    onDecideSuggestions,
}) => {
    const [loadingKey, setLoadingKey] = useState(null);
    const { voidedSuggestion, fieldItems } = getPendingReextractSuggestions(record);
    if (!voidedSuggestion && fieldItems.length === 0) return null;

    const runDecision = async (key, fields, decision) => {
        if (!onDecideSuggestions) return;
        setLoadingKey(key);
        try {
            await onDecideSuggestions(fields, decision);
        } finally {
            setLoadingKey(null);
        }
    };

    const columns = [
        {
            title: '字段',
            dataIndex: 'field',
            width: 150,
            render: (f) => <Text strong>{fieldLabels[f] || f}</Text>,
        },
        {
            title: '当前保留值',
            dataIndex: 'oldValue',
            render: (v) => <Text code>{v != null && v !== '' ? String(v) : '空'}</Text>,
        },
        {
            title: '重识别建议值',
            dataIndex: 'newValue',
            render: (v) => <Text type="warning" strong>{v != null && v !== '' ? String(v) : '空'}</Text>,
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            render: (_, row) => (
                <Space size={4}>
                    <Button
                        size="small"
                        type="link"
                        icon={<CheckOutlined />}
                        disabled={disabled || loadingKey !== null}
                        loading={loadingKey === `accept-${row.field}`}
                        onClick={() => runDecision(`accept-${row.field}`, [row.field], 'accepted')}
                    >
                        采纳
                    </Button>
                    <Button
                        size="small"
                        type="link"
                        danger
                        icon={<CloseOutlined />}
                        disabled={disabled || loadingKey !== null}
                        loading={loadingKey === `reject-${row.field}`}
                        onClick={() => runDecision(`reject-${row.field}`, [row.field], 'rejected')}
                    >
                        驳回
                    </Button>
                </Space>
            ),
        },
    ];

    const allFields = fieldItems.map((it) => it.field);

    return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {voidedSuggestion && (
                <Alert
                    showIcon
                    type="warning"
                    message="重识别提示：模型判断该页可能为作废页"
                    description="因本记录已确认或含人工复核保护，系统未自动将其改为作废状态。如确认无需作废，可点击忽略。"
                    action={(
                        <Button
                            size="small"
                            disabled={disabled || loadingKey !== null}
                            loading={loadingKey === 'ignore-void'}
                            onClick={() => runDecision('ignore-void', ['_page_voided'], 'rejected')}
                        >
                            忽略提示
                        </Button>
                    )}
                />
            )}
            {fieldItems.length > 0 && (
                <Card
                    size="small"
                    style={{ borderColor: '#ffe58f', background: '#fffbe6' }}
                    title={(
                        <Space>
                            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                            <Text strong>重识别差异建议（人工/已确认字段保护中，共 {fieldItems.length} 项）</Text>
                        </Space>
                    )}
                    extra={(
                        <Space size={6}>
                            <Button
                                size="small"
                                type="primary"
                                disabled={disabled || loadingKey !== null}
                                loading={loadingKey === 'accept-all'}
                                onClick={() => runDecision('accept-all', allFields, 'accepted')}
                            >
                                全部采纳
                            </Button>
                            <Button
                                size="small"
                                disabled={disabled || loadingKey !== null}
                                loading={loadingKey === 'reject-all'}
                                onClick={() => runDecision('reject-all', allFields, 'rejected')}
                            >
                                全部驳回
                            </Button>
                        </Space>
                    )}
                >
                    <Table
                        size="small"
                        rowKey="field"
                        pagination={false}
                        columns={columns}
                        dataSource={fieldItems}
                    />
                </Card>
            )}
        </Space>
    );
};
